import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import FormFieldTextArea from '@/components/FormFieldTextArea';
import type { HiAttemptVacancyChecklistValues } from '@/pages/homeInspection/hiAttempt.schema';

type VacancyFormShape = {
  vacancyChecklist?: HiAttemptVacancyChecklistValues | null;
};

type ChecklistItem = {
  key: keyof HiAttemptVacancyChecklistValues;
  label: string;
  detailsKey?: keyof HiAttemptVacancyChecklistValues;
  detailsLabel?: string;
};

const CHECKLIST_ITEMS: ChecklistItem[] = [
  { key: 'overgrownOrNeglectedVegetation', label: 'Overgrown or neglected vegetation' },
  {
    key: 'hazardousNoxiousOrUnhealthySubstances',
    label: 'The accumulation of hazardous, noxious, or unhealthy substances or materials on the property',
  },
  {
    key: 'newspapersCircularsFlyersOrMail',
    label: 'The accumulation of newspapers, circulars, flyers or mail on the property',
  },
  {
    key: 'utilitiesDisconnected',
    label: 'Disconnected gas, electric, or water utility services to the property',
  },
  {
    key: 'junkLitterTrashOrDebris',
    label: 'The accumulation of junk, litter, trash or debris on the property',
  },
  {
    key: 'noWindowTreatments',
    label: 'The absence of window treatments such as blinds, curtains or shutters',
  },
  {
    key: 'noFurnishingsOrPersonalItems',
    label: 'The absence of furnishings and personal items',
  },
  {
    key: 'neighborStatements',
    label:
      'Statements of neighbors, delivery persons, or government employees indicating that the residence is vacant and abandoned',
    detailsKey: 'neighborStatementsSummary',
    detailsLabel: 'Statements summary',
  },
  {
    key: 'boardedOrBrokenWindows',
    label:
      'Windows or entrances to the property that are boarded up or closed off or multiple window panes that are damaged, broken and unrepaired',
  },
  {
    key: 'brokenOrUnlockedDoors',
    label: 'Doors to the property that are smashed through, broken off, unhinged, or continuously unlocked',
  },
  {
    key: 'riskToHealthSafetyOrWelfare',
    label:
      'A risk to the health, safety or welfare of the public, or any adjoining or adjacent property owners, exists due to acts of vandalism, loitering, criminal conduct, or the physical destruction or deterioration of the property',
    detailsKey: 'riskToHealthSafetyOrWelfareDetails',
    detailsLabel: 'Risk details',
  },
  {
    key: 'codeViolationOrVacancyOrder',
    label:
      'An uncorrected violation of a municipal building, housing, or similar code during the preceding year, or an order by municipal authorities declaring the property to be unfit for occupancy and to remain vacant and unoccupied',
  },
  {
    key: 'winterizedByMortgagee',
    label:
      'The mortgagee or other authorized party has secured or winterized the property due to the property being deemed vacant and unprotected or in danger of freezing',
  },
  {
    key: 'writtenStatementOfAbandonment',
    label: 'A written statement of abandonment by the owner or other authorized party',
  },
  {
    key: 'otherIndiciaOfAbandonment',
    label: 'Any other indicia of abandonment',
    detailsKey: 'otherIndiciaOfAbandonmentDetails',
    detailsLabel: 'Other abandonment details',
  },
];

type Props = {
  readOnly?: boolean;
};

export const PsHiVacancyChecklistStep = ({ readOnly }: Props) => {
  const { control } = useFormContext<VacancyFormShape>();
  const checklist = useWatch({ control, name: 'vacancyChecklist' });

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      <div className="w-full gap-0 flex flex-col rounded-md border-bordercolor border-[1px]">
        <div className="rounded-t-md font-semibold bg-[#F5F5F5] px-4 py-4">
          <span>Vacancy Checklist</span>
        </div>
        <div className="rounded-b-md flex flex-col px-4 py-4 gap-5">
          {CHECKLIST_ITEMS.map((item) => {
            const checked = Boolean(checklist?.[item.key]);
            return (
              <div key={item.key} className="flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <Controller
                    control={control}
                    name={`vacancyChecklist.${item.key}`}
                    render={({ field }) => (
                      <Checkbox
                        id={`vacancy-${item.key}`}
                        checked={Boolean(field.value)}
                        disabled={readOnly}
                        onCheckedChange={(value) => field.onChange(value === true)}
                        className="mt-0.5"
                      />
                    )}
                  />
                  <Label
                    htmlFor={`vacancy-${item.key}`}
                    className="cursor-pointer font-normal text-sm leading-5 text-black"
                  >
                    {item.label}
                  </Label>
                </div>
                {item.detailsKey && checked && (
                  <div className="pl-7">
                    <FormFieldTextArea<VacancyFormShape>
                      name={`vacancyChecklist.${item.detailsKey}`}
                      label={item.detailsLabel ?? 'Details'}
                      placeholder="Enter details"
                      rows={3}
                      required
                      disabled={readOnly}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
